import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Flag, Loader2 } from "lucide-react";
import { call, getJson, mediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { locDoc, useLang } from "@/lib/i18n";
import { useIsMine } from "@/components/community/shared";
import { ReportDialog } from "@/components/community/PostActions";
import { NewsActions, SourceFlag, useNewsDate, useNewsSaved } from "@/components/news/NewsCard";
import { Byline, ReplyForm } from "@/pages/Topic";

const Comment = ({ c, onDelete }) => {
  const { t } = useLang();
  const { member } = useAuth();
  const mine = useIsMine()(c);
  const [reporting, setReporting] = useState(false);
  return (
    <div className="rounded-2xl border border-white/10 bg-[var(--card)] p-4" data-testid={`news-comment-item-${c.id}`}>
      <Byline doc={c} onDelete={() => onDelete(c.id)} testid={`news-comment-delete-${c.id}`} />
      <p className="mt-3 whitespace-pre-line text-sm leading-relaxed">{c.body}</p>
      {member && !mine && (
        <button type="button" onClick={() => setReporting(true)} className="post-act mt-2" aria-label={t("report.title")} title={t("report.title")} data-testid={`news-comment-report-${c.id}`}><Flag size={13} /></button>
      )}
      {reporting && <ReportDialog post={c} kind="comment" open={reporting} setOpen={setReporting} />}
    </div>
  );
};

export default function NewsDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const date = useNewsDate();
  const { saved, toggle } = useNewsSaved();
  const [data, setData] = useState(undefined);
  useEffect(() => {
    getJson(`/news/${id}`).then(setData);
  }, [id]);
  const del = async (cid) => {
    if (!window.confirm(t("com.confirmDelete"))) return;
    await call(`/community/comment/${cid}`, { method: "DELETE" });
    setData((d) => ({ ...d, comments: d.comments.filter((c) => c.id !== cid), news: { ...d.news, comment_count: Math.max(0, (d.news.comment_count ?? 1) - 1) } }));
  };
  if (data === undefined) return <main className="grid min-h-screen place-items-center"><Loader2 className="animate-spin" /></main>;
  const n = data ? locDoc(data.news, lang) : null;
  return (
    <main className="px-5 pb-28 pt-32 md:px-10" data-testid="news-detail-page">
      <div className="mx-auto max-w-3xl">
        <Link to="/noticias" className="mono inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] hover:text-[var(--ink)]" data-testid="news-back-link"><ArrowLeft size={14} /> {t("nav.news")}</Link>
        {!n ? <p className="font-display mt-10 text-3xl" data-testid="news-not-found">{t("news.notFound")}</p> : (
          <>
            <article className="mt-8" data-testid="news-main">
              <p className="mono text-[11px] uppercase tracking-[0.16em] text-[var(--green)]"><SourceFlag country={n.country} testid="news-detail-flag" /> {n.source} <span className="text-[var(--muted)]">· {date(n.published_at)}</span></p>
              <h1 className="font-display mt-2 text-4xl leading-tight sm:text-5xl" data-testid="news-detail-title">{n.title}</h1>
              {n.image_url && (
                <figure className="mt-8">
                  <img src={mediaUrl(n.image_url)} alt="" className="w-full rounded-2xl" data-testid="news-detail-image" />
                  <figcaption className="mono mt-2 text-[11px] text-[var(--muted)]" data-testid="news-detail-credit">{t("news.credit", { source: n.source })}</figcaption>
                </figure>
              )}
              <p className="mt-8 text-base leading-relaxed md:text-lg" data-testid="news-detail-summary">{n.summary}</p>
              <a href={n.url} target="_blank" rel="noopener noreferrer" className="btn-primary mt-8" data-testid="news-detail-source-btn">{t("news.readAt", { source: n.source })} <ExternalLink size={15} /></a>
              <div className="mt-8 border-y border-white/10 py-3"><NewsActions n={n} saved={saved.includes(n.id)} onSave={toggle} showSource={false} /></div>
            </article>
            <section id="comentarios" className="mt-14 scroll-mt-24" data-testid="news-comments">
              <h2 className="font-display text-2xl">{t("news.comments", { n: data.comments.length })}</h2>
              <div className="mt-4 space-y-4">{data.comments.map((c) => <Comment key={c.id} c={c} onDelete={del} />)}</div>
              <div className="mt-8">
                <ReplyForm endpoint={`/news/${n.id}/comments`} next={`/noticias/${n.id}`} testid="news-comment"
                  onReplied={(c) => setData((d) => ({ ...d, comments: [...d.comments, c], news: { ...d.news, comment_count: (d.news.comment_count ?? 0) + 1 } }))} />
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
